/**
 * Parameter Resolver + Binding Resolver — 정의(JSON) + 파라미터 값 → 숫자만 남은 형상.
 *   ① 파라미터 = param_schema 기본값 + 받은 값(범위 · 선택지로 맞춤)
 *   ② part 의 숫자 칸 식("$params.x") 계산
 *   ② derived(파생 값) — 식 또는 선택 값 → 숫자 표, 순서대로 → $vars.이름
 *   ③ bindings 를 순서대로 덮어쓴다(같은 칸이면 뒤의 것)
 * 단위 mm 는 여기서 m 로 바꾼다. 회전은 도 → 라디안. 렌더러(build.ts)는 이 결과만 본다.
 */
import { compileExpr, evalNum } from "./expr";
import type { AnimationDef, BindingDef, NumExpr, ParamSchemaEntry, PartDef, StoredDefinition, Vec3 } from "./types";
import type { FlowValues, ProfileValues } from "./context";

export type Num3 = [number, number, number];
export interface ResolvedPart {
  id: string;
  type: PartDef["type"];
  size: Num3;
  position: Num3;
  /** 라디안 */
  rotation: Num3;
  material: string | null;
  parent: string | null;
  visible: boolean;
  /** part.preview_only — 미리보기 전용(트윈 캔버스에서는 숨김) */
  previewOnly?: boolean;
  /** 전체 개수 = counts 곱 */
  count: number;
  offset: Num3;
  /** 격자 반복 — [count, count2, count3] · [offset, offset2, offset3] (count2 · count3 없으면 1) */
  counts: Num3;
  offsets: [Num3, Num3, Num3];
  /** 첫 축을 따라 하나씩 커지는 크기(없으면 0) */
  sizeStep: Num3;
  /** repeat.visible_if 가 있으면 도형마다 보이는지(없으면 모두 보임) */
  itemVisible?: boolean[];
  /** repeat.item_position · item_rotation — 도형마다 더하는 위치(m) · 회전(라디안) */
  itemPos?: Num3[];
  itemRot?: Num3[];
  /** repeat.item_scale — 도형마다 크기 배율 */
  itemScl?: Num3[];
  /** kit — 키트 이름 · 계산한 선택 값 */
  kit?: string;
  kitOpts?: Record<string, number | string>;
  /** [고정밀] 가까이만(hi) · 멀리만(lo) */
  lod?: "hi" | "lo";
  /** mesh — m 단위 꼭짓점 · 삼각형 번호 */
  vertices?: number[];
  indices?: number[];
}
export interface ResolvedPath { id: string; closed: boolean; points: Num3[] }
export interface Resolved {
  params: Record<string, number | string | boolean>;
  /** derived 계산 결과($vars) */
  vars: Record<string, number>;
  parts: ResolvedPart[];
  paths: ResolvedPath[];
  /** 바닥 크기(m) — 맨 위 part 들의 x · z 범위 */
  footprint: [number, number];
  /** [STEP 2-B] 맨 위 parts 의 경계 상자(m · 회전 무시 근사) — parts 가 없으면 null */
  partsBox?: { min: Num3; max: Num3 } | null;
  height: number;
}

export const MAX_REPEAT = 2000;
/** kit_options — 숫자 · 식은 계산, 그 밖의 글자(색 이름 등)는 그대로 */
function kitOptions(o: Record<string, NumExpr> | undefined, env: Env): Record<string, number | string> {
  const out: Record<string, number | string> = {};
  for (const [k, v] of Object.entries(o || {})) {
    if (typeof v === "number") out[k] = v;
    else if (typeof v === "string" && /[-$\d(.]/.test(v.trim()[0] || "")) { const n = evalNum(v, env); out[k] = Number.isFinite(n) ? n : v; }
    else out[k] = String(v);
  }
  return out;
}
/** n 번째 반복 도형의 자리(격자면 i + c1·(j + c2·k)) */
/** repeat.visible_if — 도형마다 i · j · k(축 번호)를 넣어 계산(0 이면 숨김) */
function itemVisibility(expr: NumExpr, counts: Num3, env: Env): boolean[] {
  const total = Math.min(MAX_REPEAT, counts[0] * counts[1] * counts[2]);
  const vars: Record<string, number> = { ...(env.vars || {}), i: 0, j: 0, k: 0 };
  const e = { params: env.params, vars, profile: env.profile };
  const out: boolean[] = new Array(total);
  for (let n = 0; n < total; n++) {
    vars.i = n % counts[0]; vars.j = Math.floor(n / counts[0]) % counts[1]; vars.k = Math.floor(n / (counts[0] * counts[1]));
    const v = evalNum(expr, e);
    out[n] = Number.isFinite(v) && v !== 0;
  }
  return out;
}

/** repeat.item_position · item_rotation — 도형마다 [x,y,z] 식(i · j · k) */
function itemVectors(v: Vec3, counts: Num3, env: Env, scale: number): Num3[] {
  const total = Math.min(MAX_REPEAT, counts[0] * counts[1] * counts[2]);
  const vars: Record<string, number> = { ...(env.vars || {}), i: 0, j: 0, k: 0 };
  const e: Env = { params: env.params, vars, profile: env.profile };
  const out: Num3[] = new Array(total);
  for (let n = 0; n < total; n++) {
    vars.i = n % counts[0]; vars.j = Math.floor(n / counts[0]) % counts[1]; vars.k = Math.floor(n / (counts[0] * counts[1]));
    out[n] = vec(v, e, 0).map((x) => x * scale) as Num3;
  }
  return out;
}

/** n 번째 반복 도형의 크기(size + size_step × 첫 축 번호) */
export function itemSize(p: Pick<ResolvedPart, "counts" | "size" | "sizeStep">, n: number): Num3 {
  const i = n % p.counts[0];
  return [p.size[0] + p.sizeStep[0] * i, p.size[1] + p.sizeStep[1] * i, p.size[2] + p.sizeStep[2] * i];
}
export function itemOffset(p: Pick<ResolvedPart, "counts" | "offsets">, n: number): Num3 {
  const [c1, c2] = p.counts;
  const i = n % c1; const j = Math.floor(n / c1) % c2; const k = Math.floor(n / (c1 * c2));
  const [a, b, c] = p.offsets;
  return [a[0] * i + b[0] * j + c[0] * k, a[1] * i + b[1] * j + c[1] * k, a[2] * i + b[2] * j + c[2] * k];
}
const AXES = { x: 0, y: 1, z: 2 } as const;

/** param_schema 로 값 맞추기(트윈 sanitizeParam 과 같은 규칙) */
export function paramValue(e: ParamSchemaEntry, v: unknown): number | string | boolean {
  if (e.type === "int" || e.type === "float") {
    let n = Number(v);
    if (!Number.isFinite(n)) n = Number(e.default);
    if (e.type === "int") n = Math.round(n);
    if (typeof e.min === "number") n = Math.max(e.min, n);
    if (typeof e.max === "number") n = Math.min(e.max, n);
    return n;
  }
  if (e.type === "select") {
    const opts = (e.options || []).map((o) => String(o.value));
    return opts.includes(String(v)) ? String(v) : String(e.default);
  }
  return typeof v === "boolean" ? v : Boolean(e.default);
}
export function resolveParams(schema: ParamSchemaEntry[], input: Record<string, unknown> | undefined): Record<string, number | string | boolean> {
  const out: Record<string, number | string | boolean> = {};
  for (const e of schema) out[e.key] = paramValue(e, input && input[e.key] !== undefined ? input[e.key] : e.default);
  return out;
}

type Env = { params: Record<string, unknown>; vars: Record<string, number>; profile?: ProfileValues };
const vec = (v: Vec3 | undefined, env: Env, fallback: number): Num3 =>
  (v ? [0, 1, 2].map((i) => { const n = evalNum(v[i], env); return Number.isFinite(n) ? n : fallback; }) : [fallback, fallback, fallback]) as Num3;

/** derived — 순서대로 계산(뒤의 것은 앞의 것을 쓴다). 계산이 안 되면 NaN */
// 같은 정의 · 같은 파라미터 객체면 다시 계산하지 않는다(매 프레임 동작 · 순서가 부른다). 돌려준 값은 고치지 말 것
const VARS_MEMO = new WeakMap<object, WeakMap<object, { profile?: ProfileValues; vars: Record<string, number> }>>();
export function computeVars(def: Pick<StoredDefinition, "derived">, params: Record<string, unknown>, profile?: ProfileValues): Record<string, number> {
  const key = def.derived;
  if (!key || !key.length) return {};
  let byParams = VARS_MEMO.get(key);
  const hit = byParams && byParams.get(params);
  if (hit && hit.profile === profile) return hit.vars;
  const vars = calcVars(def, params, profile);
  if (!byParams) { byParams = new WeakMap(); VARS_MEMO.set(key, byParams); }
  byParams.set(params, { profile, vars });
  return vars;
}
function calcVars(def: Pick<StoredDefinition, "derived">, params: Record<string, unknown>, profile?: ProfileValues): Record<string, number> {
  const vars: Record<string, number> = {};
  for (const d of def.derived || []) {
    if ("lookup" in d) {
      const key = String(params[d.lookup]);
      vars[d.name] = d.map && Object.prototype.hasOwnProperty.call(d.map, key) ? Number(d.map[key]) : typeof d.default === "number" ? d.default : NaN;
    } else vars[d.name] = evalNum(d.expr, { params, vars, profile });
  }
  return vars;
}

/** binding 값 = source 값 → expression → transform */
export function bindingValue(b: BindingDef, params: Record<string, unknown>, vars: Record<string, number> = {}): number {
  const key = b.source.replace(/^params\./, "");
  const raw = params[key];
  let v = typeof raw === "boolean" ? (raw ? 1 : 0) : Number(raw);
  if (b.expression) {
    const c = compileExpr(b.expression);
    v = c.ok ? c.expr.eval({ value: v, params, vars }) : NaN;
  }
  if (b.transform === "int") v = Math.round(v);
  return v;
}

/** target 문자열 풀기 — 검사기와 렌더러가 같이 쓴다 */
export type Target =
  | { kind: "part"; id: string; field: "size" | "position" | "rotation" | "offset"; axis: 0 | 1 | 2 }
  | { kind: "count"; id: string }
  | { kind: "anim"; id: string; field: "speed" | "distance" }
  | { kind: "asset"; node: string };
export function parseTarget(t: string): Target | null {
  let m = /^geometry\.parts\.([A-Za-z0-9_-]+)\.(size|position|rotation)\.([xyz])$/.exec(t);
  if (m) return { kind: "part", id: m[1], field: m[2] as "size" | "position" | "rotation", axis: AXES[m[3] as "x" | "y" | "z"] };
  m = /^geometry\.parts\.([A-Za-z0-9_-]+)\.repeat\.offset\.([xyz])$/.exec(t);
  if (m) return { kind: "part", id: m[1], field: "offset", axis: AXES[m[2] as "x" | "y" | "z"] };
  m = /^geometry\.parts\.([A-Za-z0-9_-]+)\.repeat\.count$/.exec(t);
  if (m) return { kind: "count", id: m[1] };
  m = /^motion\.animations\.([A-Za-z0-9_-]+)\.(speed|distance)$/.exec(t);
  if (m) return { kind: "anim", id: m[1], field: m[2] as "speed" | "distance" };
  m = /^asset\.nodes\.([A-Za-z0-9_-]+)\.(scale|position|rotation)\.([xyz])$/.exec(t);
  if (m) return { kind: "asset", node: m[1] };
  return null;
}

/** profile = 트윈 운영 프로필 값($profile — 없으면 기본 프로필) */
export function resolveDefinition(def: StoredDefinition, input?: Record<string, unknown>, ctx: { profile?: ProfileValues } = {}): Resolved {
  const params = resolveParams(def.param_schema || [], input);
  const vars = computeVars(def, params, ctx.profile);
  const env: Env = { params, vars: { pi: 0, ...vars }, profile: ctx.profile }; // pi = 부모 반복 번호(live part — 처음 값 0 · 매 프레임 build 가 다시)
  const geo = def.geometry || {};
  const k = geo.unit === "mm" ? 0.001 : 1;
  const cnt = (v: unknown) => Math.max(0, Math.round(evalNum(v, env) || 0));
  const parts: ResolvedPart[] = (geo.parts || []).map((p) => {
    const size = vec(p.size, env, 0);
    const vis = p.visible === undefined ? true : typeof p.visible === "boolean" ? p.visible : evalNum(p.visible, env) !== 0;
    const r = p.repeat;
    const counts: Num3 = r ? [cnt(r.count), r.count2 === undefined ? 1 : cnt(r.count2), r.count3 === undefined ? 1 : cnt(r.count3)] : [1, 1, 1];
    const offset = r && r.offset ? vec(r.offset, env, 0) : ([0, size[1], 0] as Num3);
    return {
      id: p.id, type: p.type,
      size, position: vec(p.position, env, 0), rotation: vec(p.rotation, env, 0),
      material: p.material || null, parent: p.parent || null, visible: vis,
      ...(p.preview_only === true ? { previewOnly: true } : {}),
      ...(p.lod === "hi" || p.lod === "lo" ? { lod: p.lod } : {}),
      ...(p.type === "kit" ? { kit: String(p.kit || ""), kitOpts: kitOptions(p.kit_options, env) } : {}),
      count: Math.min(MAX_REPEAT, counts[0] * counts[1] * counts[2]),
      offset,
      counts,
      offsets: [offset, r && r.offset2 ? vec(r.offset2, env, 0) : [0, 0, 0], r && r.offset3 ? vec(r.offset3, env, 0) : [0, 0, 0]],
      sizeStep: r && r.size_step ? vec(r.size_step, env, 0) : [0, 0, 0],
      ...(r && r.visible_if !== undefined ? { itemVisible: itemVisibility(r.visible_if, counts, env) } : {}),
      ...(r && r.item_position ? { itemPos: itemVectors(r.item_position, counts, env, k) } : {}),
      ...(r && r.item_rotation ? { itemRot: itemVectors(r.item_rotation, counts, env, Math.PI / 180) } : {}),
      ...(r && r.item_scale ? { itemScl: itemVectors(r.item_scale, counts, env, 1) } : {}),
      ...(p.type === "mesh" ? { vertices: (p.vertices || []).map((n) => n * k), ...(p.indices ? { indices: p.indices } : {}) } : {}),
    };
  });
  const byId = new Map(parts.map((p) => [p.id, p]));
  for (const b of def.bindings || []) {
    const t = parseTarget(b.target);
    if (!t || t.kind === "anim" || t.kind === "asset") continue; // 동작 값은 매 프레임(animNumber) · asset 은 예정
    const part = byId.get(t.id);
    if (!part) continue;
    const v = bindingValue(b, params, vars);
    if (!Number.isFinite(v)) continue;
    if (t.kind === "count") { part.counts[0] = Math.max(0, Math.round(v)); part.count = Math.min(MAX_REPEAT, part.counts[0] * part.counts[1] * part.counts[2]); }
    else part[t.field][t.axis] = v; // offset 은 offsets[0] 과 같은 배열
  }
  // 단위 · 각도
  for (const p of parts) {
    p.size = p.size.map((n) => n * k) as Num3;
    p.sizeStep = p.sizeStep.map((n) => n * k) as Num3;
    p.position = p.position.map((n) => n * k) as Num3;
    p.offsets = p.offsets.map((o) => o.map((n) => n * k)) as [Num3, Num3, Num3];
    p.offset = p.offsets[0];
    p.rotation = p.rotation.map((d) => (d * Math.PI) / 180) as Num3;
  }
  const paths: ResolvedPath[] = ((def.motion && def.motion.paths) || []).map((path) => ({
    id: path.id, closed: path.type === "closed",
    points: (path.points || []).map((pt) => vec(pt, env, 0).map((n) => n * k) as Num3),
  }));
  // 바닥 크기 — 맨 위(부모 없는) part 의 반복까지 포함한 x · z 범위(회전은 무시한 근사)
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, h = 0, y0 = Infinity;
  for (const p of parts) {
    if (p.parent || !p.visible || p.count < 1 || p.lod === "hi") continue; // 고정밀 부품은 바닥 크기에 넣지 않는다
    // mesh 는 꼭짓점 범위(회전 무시 근사), 나머지는 size
    let lo: Num3 = [-p.size[0] / 2, -p.size[1] / 2, -(p.type === "cylinder" ? p.size[0] : p.size[2]) / 2];
    let hi: Num3 = [p.size[0] / 2, p.type === "plane" ? 0 : p.size[1] / 2, (p.type === "cylinder" ? p.size[0] : p.size[2]) / 2];
    if (p.type === "mesh" && p.vertices && p.vertices.length) {
      lo = [Infinity, Infinity, Infinity]; hi = [-Infinity, -Infinity, -Infinity];
      for (let i = 0; i < p.vertices.length; i += 3) for (let a = 0; a < 3; a++) { lo[a] = Math.min(lo[a], p.vertices[i + a]); hi[a] = Math.max(hi[a], p.vertices[i + a]); }
    }
    // 도형마다 위치를 더하면(item_position) 모두 본다 · 아니면 처음 · 끝 둘만(격자 끝)
    const idx = p.itemPos ? Array.from({ length: p.count }, (_, n) => n) : [0, p.count - 1];
    for (const n of idx) {
      const o = itemOffset(p, n);
      if (p.itemPos && p.itemPos[n]) { o[0] += p.itemPos[n][0]; o[1] += p.itemPos[n][1]; o[2] += p.itemPos[n][2]; }
      if (p.type !== "mesh" && (p.sizeStep[0] || p.sizeStep[1] || p.sizeStep[2])) {
        const z = itemSize(p, n); const zd = p.type === "cylinder" ? z[0] : z[2];
        lo = [-z[0] / 2, -z[1] / 2, -zd / 2]; hi = [z[0] / 2, p.type === "plane" ? 0 : z[1] / 2, zd / 2];
      }
      const cx = p.position[0] + o[0]; const cy = p.position[1] + o[1]; const cz = p.position[2] + o[2];
      x0 = Math.min(x0, cx + lo[0]); x1 = Math.max(x1, cx + hi[0]);
      z0 = Math.min(z0, cz + lo[2]); z1 = Math.max(z1, cz + hi[2]);
      h = Math.max(h, cy + hi[1]); y0 = Math.min(y0, cy + lo[1]);
    }
  }
  const footprint: [number, number] = Number.isFinite(x0) ? [Math.max(0.1, x1 - x0), Math.max(0.1, z1 - z0)] : [1, 1];
  // 정의가 바닥 크기를 적었으면 그 값(코드판과 같은 배치 크기 — 통로 표시 · 작업자 자리는 빼고)
  if (Array.isArray(geo.footprint) && geo.footprint.length === 2) {
    const f = geo.footprint.map((v) => evalNum(v, env) * k);
    if (f.every((n) => Number.isFinite(n) && n > 0)) { footprint[0] = f[0]; footprint[1] = f[1]; }
  }
  // [STEP 2-B] parts 경계 상자(회전 무시 근사 — 위와 같은 범위) — GLB 와 혼합일 때 「3D 모델」 크기에 합친다. parts 없으면 null
  const partsBox: Resolved["partsBox"] = Number.isFinite(x0) ? { min: [x0, Math.min(0, y0), z0], max: [x1, h, z1] } : null;
  return { params, vars, parts, paths, footprint, height: h, partsBox };
}

/** 동작 숫자(speed · distance) — 매 프레임 지금 파라미터로. binding 이 있으면 binding 이 이긴다 */
export function animNumber(def: StoredDefinition, anim: AnimationDef, field: "speed" | "distance", params: Record<string, unknown>, fallback: number, ctx: { profile?: ProfileValues; flow?: FlowValues } = {}): number {
  const vars = computeVars(def, params, ctx.profile);
  let v = evalNum(anim[field], { params, vars, profile: ctx.profile, flow: ctx.flow });
  for (const b of def.bindings || []) {
    const t = parseTarget(b.target);
    if (t && t.kind === "anim" && t.id === anim.id && t.field === field) v = bindingValue(b, params, vars);
  }
  if (!Number.isFinite(v)) return fallback;
  // 길이 단위인 것만 mm → m (translate 거리 · translate/path 속도). 회전은 도, 깜빡임은 횟수
  const lengthy = (field === "distance" && anim.type === "translate") || (field === "speed" && (anim.type === "translate" || anim.type === "path"));
  return lengthy && def.geometry && def.geometry.unit === "mm" ? v * 0.001 : v;
}
